import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import ReactQuill from "react-quill-new";
import Select from "react-select";
import Swal from "sweetalert2";
import { HexColorPicker } from "react-colorful";
import { getCategories, storeProduct } from "../Redux/Reducers/ProductSlice";

const AddProductForm = () => {
  const dispatch = useDispatch();
  const {
    categories = [],
    categoriesLoading = false,
    submitting = false,
  } = useSelector(
    (state) =>
      state.products || {
        categories: [],
        categoriesLoading: false,
        submitting: false,
      },
  );

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    category_id: "",
    type: "",
    buy_price: "",
    rent_price: "",
    buy_style: "",
    rent_style: "",
    color_name: "",
    color_code: "#000000",
    colors: [],
    featured_image: null,
    featured_image_color: null,
    is_featured: false,
    gallery_images: [],
    gallery_image_colors: [],
    coat_sizes: [],
    coat_measurements: [],
    pant_sizes: [],
    pant_measurements: [],
    sizes: [],
    shoe_sizes: [],
  });

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [galleryImagePreviews, setGalleryImagePreviews] = useState([]);
  const [pendingGalleryImages, setPendingGalleryImages] = useState([]);
  const [pendingGalleryImagePreviews, setPendingGalleryImagePreviews] =
    useState([]);
  const [pendingGalleryImageColor, setPendingGalleryImageColor] =
    useState(null);
  const [editingColorIndex, setEditingColorIndex] = useState(null);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [shoeSizeOptions, setShoeSizeOptions] = useState([]);
  const featuredImageInputRef = useRef(null);
  const galleryImagesInputRef = useRef(null);

  // Suits tabs states
  const [colorSizeSelections, setColorSizeSelections] = useState({});

  const typeOptions = [
    { value: "buy", label: "Buy Only" },
    { value: "rent", label: "Rent Only" },
    { value: "both", label: "Both (Buy & Rent)" },
  ];

  useEffect(() => {
    dispatch(getCategories());
  }, [dispatch]);

  // Generate slug from name
  useEffect(() => {
    if (formData.name) {
      const slug = formData.name
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/--+/g, "-")
        .trim();
      setFormData((prev) => ({ ...prev, slug }));
    }
  }, [formData.name]);

  // Handle category change
  const handleCategoryChange = (selectedOption) => {
    const category = categories.find((c) => c.id === selectedOption.value);
    setSelectedCategory(category);

    setFormData((prev) => {
      const firstColor = prev.colors[0];

      return {
        ...prev,
        category_id: selectedOption.value,
        type: "",
        buy_price: "",
        rent_price: "",
        ...(category.name === "Suits" && firstColor
          ? {
              color_name: firstColor.name,
              color_code: firstColor.code,
              buy_style: firstColor.buy_style || "",
              rent_style: firstColor.rent_style || "",
              colors: [],
            }
          : {}),
        coat_sizes: [],
        coat_measurements: [],
        pant_sizes: [],
        pant_measurements: [],
        sizes: [],
        shoe_sizes: [],
      };
    });

    setColorSizeSelections({});
    setEditingColorIndex(null);

    setSizeOptions([]);
    setShoeSizeOptions([]);

    if (category.name === "Suits") {
      // No need to set options, tabs will handle it
    } else if (["Shirts", "Ties", "Belts"].includes(category.name)) {
      const sizes = category?.size_options?.data?.size_types || [];
      setSizeOptions(sizes.map((s) => ({ value: s, label: s })));
    } else if (["Bow Ties", "Pocket Square", "Socks"].includes(category.name)) {
      setSizeOptions([{ value: "Standard", label: "Standard" }]);
      setFormData((prev) => ({
        ...prev,
        sizes: [{ value: "Standard", label: "Standard" }],
      }));
    } else if (category.name === "Shoes") {
      const measurements = category?.size_options?.data?.measurements || [];
      setShoeSizeOptions(measurements.map((m) => ({ value: m, label: m })));
    }
  };

  // Handle featured image change
  const handleFeaturedImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, featured_image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setFeaturedImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle gallery images change
  const handleGalleryImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setPendingGalleryImages(files);

      const newPreviews = [];
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result);
          if (newPreviews.length === files.length) {
            setPendingGalleryImagePreviews(newPreviews);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const getImageColorValue = (colorName) => {
    const trimmedColorName = colorName.trim();
    const matchedColor = getSelectedColors().find(
      (color) => color.name.toLowerCase() === trimmedColorName.toLowerCase(),
    );

    if (!trimmedColorName) return null;

    return {
      value: matchedColor?.name || trimmedColorName,
      label: matchedColor?.name || trimmedColorName,
      code: matchedColor?.code || "",
    };
  };

  const handleFeaturedImageColorChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      featured_image_color: getImageColorValue(e.target.value),
    }));
  };

  const handlePendingGalleryImageColorChange = (selectedOption) => {
    setPendingGalleryImageColor(selectedOption);
  };

  const handleAddGalleryImages = () => {
    if (pendingGalleryImages.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please select gallery image!",
      });
      return;
    }

    if (selectedCategory?.name !== "Suits" && !pendingGalleryImageColor) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please enter gallery image color!",
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      gallery_images: [...prev.gallery_images, ...pendingGalleryImages],
      gallery_image_colors: [
        ...prev.gallery_image_colors,
        ...pendingGalleryImages.map(() =>
          selectedCategory?.name === "Suits" ? null : pendingGalleryImageColor,
        ),
      ],
    }));
    setGalleryImagePreviews((prev) => [
      ...prev,
      ...pendingGalleryImagePreviews,
    ]);
    setPendingGalleryImages([]);
    setPendingGalleryImagePreviews([]);
    setPendingGalleryImageColor(null);
  };

  // Remove gallery image
  const removeGalleryImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index),
      gallery_image_colors: prev.gallery_image_colors.filter(
        (_, i) => i !== index,
      ),
    }));
    setGalleryImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Remove featured image
  const removeFeaturedImage = () => {
    setFormData((prev) => ({
      ...prev,
      featured_image: null,
      featured_image_color: null,
    }));
    setFeaturedImagePreview(null);
  };

  const navigate = useNavigate();
  const location = useLocation();

  const handleTypeChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      type: selectedOption.value,
      buy_price: "",
      rent_price: "",
      buy_style: "",
      rent_style: "",
    }));
    setEditingColorIndex(null);
  };

  const handleBuyPriceChange = (e) => {
    setFormData((prev) => ({ ...prev, buy_price: e.target.value }));
  };

  const handleRentPriceChange = (e) => {
    setFormData((prev) => ({ ...prev, rent_price: e.target.value }));
  };

  const handleColorChange = (color) => {
    setFormData((prev) => ({ ...prev, color_code: color }));
  };

  const handleColorNameChange = (e) => {
    setFormData((prev) => ({ ...prev, color_name: e.target.value }));
  };

  const getSelectedColors = () => {
    if (selectedCategory?.name === "Suits" && formData.color_name.trim()) {
      return [
        {
          name: formData.color_name.trim(),
          code: formData.color_code,
          buy_style: formData.buy_style.trim(),
          rent_style: formData.rent_style.trim(),
        },
      ];
    }

    if (formData.colors.length > 0) return formData.colors;
    if (formData.color_name.trim()) {
      return [
        {
          name: formData.color_name.trim(),
          code: formData.color_code,
          buy_style: formData.buy_style.trim(),
          rent_style: formData.rent_style.trim(),
        },
      ];
    }
    return [];
  };

  const getColorBuyStyle = (color) => color.buy_style || "";
  const getColorRentStyle = (color) => color.rent_style || "";

  const getColorOptions = () =>
    getSelectedColors().map((color) => ({
      value: color.name,
      label: color.name,
      code: color.code,
    }));

  const handleAddColor = () => {
    const colorName = formData.color_name.trim();
    const isEditingColor = editingColorIndex !== null;

    if (!colorName) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please enter color name!",
      });
      return;
    }

    const alreadyAdded = formData.colors.some(
      (color, index) =>
        index !== editingColorIndex &&
        color.name.toLowerCase() === colorName.toLowerCase(),
    );

    if (alreadyAdded) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "This color is already added!",
      });
      return;
    }

    if ((formData.type === "buy" || formData.type === "both") && !formData.buy_style.trim()) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please enter buy style!",
      });
      return;
    }

    if ((formData.type === "rent" || formData.type === "both") && !formData.rent_style.trim()) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please enter rent style!",
      });
      return;
    }

    const nextColor = {
      name: colorName,
      code: formData.color_code,
      buy_style: formData.buy_style.trim(),
      rent_style: formData.rent_style.trim(),
    };
    const previousColor = isEditingColor ? formData.colors[editingColorIndex] : null;

    setFormData((prev) => ({
      ...prev,
      colors: isEditingColor
        ? prev.colors.map((color, index) =>
            index === editingColorIndex ? nextColor : color,
          )
        : [...prev.colors, nextColor],
      color_name: "",
      color_code: "#000000",
      buy_style: "",
      rent_style: "",
    }));

    if (previousColor && getColorKey(previousColor) !== getColorKey(nextColor)) {
      setColorSizeSelections((prev) => {
        const updatedSelections = { ...prev };
        updatedSelections[getColorKey(nextColor)] = updatedSelections[getColorKey(previousColor)];
        delete updatedSelections[getColorKey(previousColor)];
        return updatedSelections;
      });
    }

    setEditingColorIndex(null);
    setShowColorPicker(false);
  };

  const handleEditColor = (index) => {
    const color = formData.colors[index];
    if (!color) return;

    setEditingColorIndex(index);
    setFormData((prev) => ({
      ...prev,
      color_name: color.name,
      color_code: color.code,
      buy_style: color.buy_style || "",
      rent_style: color.rent_style || "",
    }));
    setShowColorPicker(false);
  };

  const handleRemoveColor = (index) => {
    const colorToRemove = formData.colors[index];
    setFormData((prev) => ({
      ...prev,
      colors: prev.colors.filter((_, colorIndex) => colorIndex !== index),
    }));
    if (editingColorIndex === index) {
      setEditingColorIndex(null);
      setFormData((prev) => ({
        ...prev,
        color_name: "",
        color_code: "#000000",
        buy_style: "",
        rent_style: "",
      }));
    } else if (editingColorIndex > index) {
      setEditingColorIndex((prev) => prev - 1);
    }
    if (colorToRemove) {
      setColorSizeSelections((prev) => {
        const updatedSelections = { ...prev };
        delete updatedSelections[getColorKey(colorToRemove)];
        return updatedSelections;
      });
    }
  };

  const getColorKey = (color) => `${color.name}-${color.code}`;

  const getEmptyColorSizeSelection = () => ({
    coatTypes: [],
    pantTypes: [],
    coatMeasurementsMap: {},
    pantMeasurementsMap: {},
    waist_measurements: [],
    outseam_measurements: [],
    sizes: [],
    shoe_sizes: [],
  });

  const getColorSizeSelection = (color) =>
    colorSizeSelections[getColorKey(color)] || getEmptyColorSizeSelection();

  const updateColorSizeSelection = (color, updater) => {
    const colorKey = getColorKey(color);
    setColorSizeSelections((prev) => {
      const currentSelection = prev[colorKey] || getEmptyColorSizeSelection();
      const nextSelection =
        typeof updater === "function"
          ? updater(currentSelection)
          : { ...currentSelection, ...updater };

      return {
        ...prev,
        [colorKey]: nextSelection,
      };
    });
  };

  const handleBuyStyleChange = (e) => {
    setFormData((prev) => ({ ...prev, buy_style: e.target.value }));
  };

  const handleRentStyleChange = (e) => {
    setFormData((prev) => ({ ...prev, rent_style: e.target.value }));
  };

  // Strip HTML tags from description
  const stripHtmlTags = (html) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };

  const getErrorMessage = (error) => {
    if (!error) return "Something went wrong while adding the product!";
    if (typeof error === "string") return error;
    if (error.message) return error.message;
    if (error.response?.data?.message) return error.response.data.message;
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      if (typeof errors === "string") return errors;
      return Object.values(errors)
        .flat()
        .map((msg) => (typeof msg === "string" ? msg : JSON.stringify(msg)))
        .join("\n");
    }
    if (error.errors) {
      const errors = error.errors;
      if (typeof errors === "string") return errors;
      return Object.values(errors)
        .flat()
        .map((msg) => (typeof msg === "string" ? msg : JSON.stringify(msg)))
        .join("\n");
    }
    return "Something went wrong while adding the product!";
  };

  const validateProduct = () => {
    if (!formData.name) return "Please enter product name!";
    if (!formData.type) return "Please select product type!";
    if (formData.type === "buy" && !formData.buy_price) return "Please enter buy price!";
    if (formData.type === "rent" && !formData.rent_price) return "Please enter rent price!";
    if (formData.type === "both" && (!formData.buy_price || !formData.rent_price)) return "Please enter both buy and rent prices!";
    if (getSelectedColors().length === 0) return "Please add at least one color!";
    const missingBuyStyle = getSelectedColors().some(
      (color) => (formData.type === "buy" || formData.type === "both") && !getColorBuyStyle(color),
    );
    const missingRentStyle = getSelectedColors().some(
      (color) => (formData.type === "rent" || formData.type === "both") && !getColorRentStyle(color),
    );
    if (missingBuyStyle) return "Please enter buy style for each color!";
    if (missingRentStyle) return "Please enter rent style for each color!";
    if (!formData.featured_image) return "Please upload featured image!";
    if (selectedCategory?.name !== "Suits" && !formData.featured_image_color) return "Please enter featured image color!";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationMessage = validateProduct();
    if (validationMessage) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: validationMessage,
      });
      return;
    }

    const submitData = new FormData();
    submitData.append("name", formData.name);
    submitData.append("slug", formData.slug);
    submitData.append("description", stripHtmlTags(formData.description));
    submitData.append("category_id", formData.category_id);
    submitData.append("buy_type", formData.type);
    const selectedColors = getSelectedColors();
    const primaryColor = selectedColors[0];

    submitData.append("color_name", primaryColor?.name || "");
    submitData.append("color_code", primaryColor?.code || "");
    selectedColors.forEach((color, index) => {
      submitData.append(`colors[${index}][name]`, color.name);
      submitData.append(`colors[${index}][code]`, color.code);
    });

    if (formData.is_featured) {
      submitData.append("is_featured", "1");
    }

    // Only send price/style fields that apply to the selected product type.
    if (formData.type === "buy" || formData.type === "both") {
      submitData.append("buy_price", formData.buy_price || "");
      submitData.append("buy_style", getColorBuyStyle(primaryColor) || "");
    }

    if (formData.type === "rent" || formData.type === "both") {
      submitData.append("rent_price", formData.rent_price || "");
      submitData.append("rent_style", getColorRentStyle(primaryColor) || "");
    }

    // Handle images - with is_primary flag - CORRECT WAY
    // Featured image (index 0, is_primary = 1)
    submitData.append("images[0][image]", formData.featured_image);
    submitData.append("images[0][is_primary]", "1");
    if (selectedCategory?.name !== "Suits") {
      submitData.append("images[0][color]", formData.featured_image_color.label);
    }

    // Gallery images
    formData.gallery_images.forEach((image, index) => {
      const imageColor = formData.gallery_image_colors[index];

      submitData.append(`images[${index + 1}][image]`, image);
      submitData.append(`images[${index + 1}][is_primary]`, "0");
      if (selectedCategory?.name !== "Suits" && imageColor) {
        submitData.append(`images[${index + 1}][color]`, imageColor.label);
      }
    });

    let variantsArray = [];

    if (selectedCategory?.name === "Suits") {
      selectedColors.forEach((color) => {
        const selection = getColorSizeSelection(color);

        selection.coatTypes.forEach((coatType) => {
          const measurements = selection.coatMeasurementsMap[coatType] || [];
          measurements.forEach((measurement) => {
            if (formData.type === "both") {
              variantsArray.push({
                size_category: "coat",
                size_type: coatType,
                size_measurement: measurement.value,
                color: color.name,
                color_code: color.code,
                buy_type: "buy",
                buy_style: getColorBuyStyle(color),
                stock_quantity: 500,
                price: formData.buy_price,
              });
              variantsArray.push({
                size_category: "coat",
                size_type: coatType,
                size_measurement: measurement.value,
                color: color.name,
                color_code: color.code,
                buy_type: "rent",
                rent_style: getColorRentStyle(color),
                stock_quantity: 500,
                price: formData.rent_price,
              });
            } else {
              variantsArray.push({
                size_category: "coat",
                size_type: coatType,
                size_measurement: measurement.value,
                color: color.name,
                color_code: color.code,
                buy_type: formData.type,
                ...(formData.type === "buy"
                  ? { buy_style: getColorBuyStyle(color) }
                  : { rent_style: getColorRentStyle(color) }),
                stock_quantity: 500,
                price: formData.type === "buy" ? formData.buy_price : formData.rent_price,
              });
            }
          });
        });

        const waistMeasurements = selection.waist_measurements || [];
        const outseamMeasurements = selection.outseam_measurements || [];
        waistMeasurements.forEach((waistMeasurement) => {
          outseamMeasurements.forEach((outseamMeasurement) => {
            if (formData.type === "both") {
              variantsArray.push({
                size_category: "pants",
                waist_measurement: waistMeasurement.value,
                outseam_measurement: outseamMeasurement.value,
                color: color.name,
                color_code: color.code,
                buy_type: "buy",
                buy_style: getColorBuyStyle(color),
                stock_quantity: 500,
                price: formData.buy_price,
              });
              variantsArray.push({
                size_category: "pants",
                waist_measurement: waistMeasurement.value,
                outseam_measurement: outseamMeasurement.value,
                color: color.name,
                color_code: color.code,
                buy_type: "rent",
                rent_style: getColorRentStyle(color),
                stock_quantity: 500,
                price: formData.rent_price,
              });
            } else {
              variantsArray.push({
                size_category: "pants",
                waist_measurement: waistMeasurement.value,
                outseam_measurement: outseamMeasurement.value,
                color: color.name,
                color_code: color.code,
                buy_type: formData.type,
                ...(formData.type === "buy"
                  ? { buy_style: getColorBuyStyle(color) }
                  : { rent_style: getColorRentStyle(color) }),
                stock_quantity: 500,
                price: formData.type === "buy" ? formData.buy_price : formData.rent_price,
              });
            }
          });
        });
      });
    } else if (
      [
        "Shirts",
        "Ties",
        "Belts",
        "Bow Ties",
        "Pocket Square",
        "Socks",
      ].includes(selectedCategory?.name)
    ) {
      selectedColors.forEach((color) => {
        const selection = getColorSizeSelection(color);
        const colorSizes = ["Bow Ties", "Pocket Square", "Socks"].includes(
          selectedCategory?.name,
        )
          ? [{ value: "Standard", label: "Standard" }]
          : selection.sizes;

        if (colorSizes && colorSizes.length > 0) {
          colorSizes.forEach((size) => {
            if (formData.type === "both") {
              // Buy variant
              variantsArray.push({
                size_type: size.value,
                color: color.name,
                color_code: color.code,
                buy_type: "buy",
                buy_style: getColorBuyStyle(color),
                stock_quantity: 500,
                price: formData.buy_price,
              });
              // Rent variant
              variantsArray.push({
                size_type: size.value,
                color: color.name,
                color_code: color.code,
                buy_type: "rent",
                rent_style: getColorRentStyle(color),
                stock_quantity: 500,
                price: formData.rent_price,
              });
            } else {
              // Single variant for buy or rent
              variantsArray.push({
                size_type: size.value,
                color: color.name,
                color_code: color.code,
                buy_type: formData.type,
                ...(formData.type === "buy"
                  ? { buy_style: getColorBuyStyle(color) }
                  : { rent_style: getColorRentStyle(color) }),
                stock_quantity: 500,
                price: formData.type === "buy" ? formData.buy_price : formData.rent_price,
              });
            }
          });
        }
      });
    } else if (selectedCategory?.name === "Shoes") {
      selectedColors.forEach((color) => {
        const selection = getColorSizeSelection(color);

        if (selection.shoe_sizes && selection.shoe_sizes.length > 0) {
          selection.shoe_sizes.forEach((size) => {
            if (formData.type === "both") {
              // Buy variant
              variantsArray.push({
                size_measurement: size.value,
                color: color.name,
                color_code: color.code,
                buy_type: "buy",
                buy_style: getColorBuyStyle(color),
                stock_quantity: 500,
                price: formData.buy_price,
              });
              // Rent variant
              variantsArray.push({
                size_measurement: size.value,
                color: color.name,
                color_code: color.code,
                buy_type: "rent",
                rent_style: getColorRentStyle(color),
                stock_quantity: 500,
                price: formData.rent_price,
              });
            } else {
              // Single variant for buy or rent
              variantsArray.push({
                size_measurement: size.value,
                color: color.name,
                color_code: color.code,
                buy_type: formData.type,
                ...(formData.type === "buy"
                  ? { buy_style: getColorBuyStyle(color) }
                  : { rent_style: getColorRentStyle(color) }),
                stock_quantity: 500,
                price: formData.type === "buy" ? formData.buy_price : formData.rent_price,
              });
            }
          });
        }
      });
    }

    if (variantsArray.length > 0) {
      variantsArray.forEach((variant, index) => {
        Object.entries(variant).forEach(([key, value]) => {
          submitData.append(`variants[${index}][${key}]`, value);
        });
      });
    } else {
      submitData.append("variants[]", "");
    }

    try {
      await dispatch(storeProduct(submitData)).unwrap();

      Swal.fire({
        icon: "success",
        title: "Product Created Successfully!",
        showConfirmButton: false,
        timer: 2000,
      }).then(() => {
        navigate(location.state?.from || "/products");
      });

      // Reset form
      setFormData({
        name: "",
        slug: "",
        description: "",
        category_id: "",
        type: "",
        buy_price: "",
        rent_price: "",
        buy_style: "",
        rent_style: "",
        color_name: "",
        color_code: "#000000",
        colors: [],
        featured_image: null,
        featured_image_color: null,
        is_featured: false,
        gallery_images: [],
        gallery_image_colors: [],
        coat_sizes: [],
        coat_measurements: [],
        pant_sizes: [],
        pant_measurements: [],
        sizes: [],
        shoe_sizes: [],
      });
      setSelectedCategory(null);
      setFeaturedImagePreview(null);
      setGalleryImagePreviews([]);
      setPendingGalleryImages([]);
      setPendingGalleryImagePreviews([]);
      setPendingGalleryImageColor(null);
      setColorSizeSelections({});
      setEditingColorIndex(null);
      setSizeOptions([]);
      setShoeSizeOptions([]);
      setShowColorPicker(false);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: getErrorMessage(error),
      });
    }
  };

  const categoryOptions = categories.map((category) => ({
    value: category.id,
    label: category.name,
  }));

  const renderColorSizeFields = () => {
    const selectedColors = getSelectedColors();

    if (!selectedCategory || selectedColors.length === 0) return null;

    return (
      <div className="mb-4">
        {selectedColors.map((color) => {
          const selection = getColorSizeSelection(color);
          const colorKey = getColorKey(color);

          return (
            <div key={colorKey} className="border rounded p-3 mb-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: color.code,
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    display: "inline-block",
                  }}
                />
                <h6 className="mb-0">{color.name} Sizes</h6>
                {/* <small className="text-muted">{color.code}</small> */}
              </div>

              {selectedCategory.name === "Suits" && (
                <>
                  <label className="form-label fw-bold mb-2 fs-4">
                    Coat Size & Measurement <span>*</span>
                  </label>
                  <div className="size-tabs">
                    {["short", "regular", "long", "xl"].map((type) => (
                      <button
                        key={`${colorKey}-coat-${type}`}
                        type="button"
                        className={`btn ${selection.coatTypes.includes(type) ? "active" : "btn-outline-secondary"}`}
                        onClick={() => {
                          updateColorSizeSelection(color, (current) => {
                            if (current.coatTypes.includes(type)) {
                              const newMap = { ...current.coatMeasurementsMap };
                              delete newMap[type];
                              return {
                                ...current,
                                coatTypes: current.coatTypes.filter((t) => t !== type),
                                coatMeasurementsMap: newMap,
                              };
                            }

                            return {
                              ...current,
                              coatTypes: [...current.coatTypes, type],
                            };
                          });
                        }}
                        style={{ minWidth: "80px" }}
                      >
                        {type.toUpperCase()}
                      </button>
                    ))}
                  </div>

                  {selection.coatTypes.map((type) => {
                    const coatData =
                      selectedCategory?.size_options?.data?.categories?.find(
                        (cat) => cat.name === "coat",
                      );
                    const sizeType = coatData?.types?.find((t) => t.name === type);
                    const measurementsList =
                      sizeType?.measurements?.map((m) => ({ value: m, label: m })) ||
                      [];

                    return (
                      <div key={`${colorKey}-coat-measurement-${type}`} className="mt-3">
                        <label className="form-label fw-bold">
                          {color.name} {type.toUpperCase()} Coat Measurements <span>*</span>
                        </label>
                        <Select
                          name={`${colorKey}_coatMeasurements_${type}`}
                          isMulti
                          closeMenuOnSelect={false}
                          required
                          options={measurementsList}
                          value={selection.coatMeasurementsMap[type] || []}
                          onChange={(selectedOptions) => {
                            updateColorSizeSelection(color, (current) => ({
                              ...current,
                              coatMeasurementsMap: {
                                ...current.coatMeasurementsMap,
                                [type]: selectedOptions || [],
                              },
                            }));
                          }}
                          placeholder={`Select ${color.name} ${type.toUpperCase()} coat measurements`}
                          isSearchable
                          className="select"
                        />
                      </div>
                    );
                  })}
                  <div className="box-line"></div>
                  <label className="form-label fw-bold fs-4">
                    Pant Measurements <span>*</span>
                  </label>
                  {(() => {
                    const pantMeasurements =
                      selectedCategory?.size_options?.data?.categories?.find(
                        (category) =>
                          category.name === "pants" ||
                          category.name === "pant" ||
                          category.measurements,
                      )?.measurements ||
                      selectedCategory?.size_options?.data?.measurements ||
                      [];
                    const waistOptions =
                      pantMeasurements
                        .find((measurement) => measurement.name === "waist_measurement")
                        ?.values?.map((value) => ({ value, label: value })) || [];
                    const outseamOptions =
                      pantMeasurements
                        .find((measurement) => measurement.name === "outseam_measurement")
                        ?.values?.map((value) => ({ value, label: value })) || [];

                    return (
                      <>
                        <div className="mt-1">
                          <label className="form-label fw-bold">
                            {color.name} Waist Measurement <span>*</span>
                          </label>
                          <Select
                            name={`${colorKey}_waist_measurements`}
                            isMulti
                            closeMenuOnSelect={false}
                            required
                            options={waistOptions}
                            value={selection.waist_measurements || []}
                            onChange={(selectedOptions) => {
                              updateColorSizeSelection(color, {
                                waist_measurements: selectedOptions || [],
                              });
                            }}
                            placeholder={`Select ${color.name} waist measurements`}
                            isSearchable
                            className="select"
                          />
                        </div>

                        <div className="mt-3">
                          <label className="form-label fw-bold">
                            {color.name} Outseam Measurement <span>*</span>
                          </label>
                          <Select
                            name={`${colorKey}_outseam_measurements`}
                            isMulti
                            closeMenuOnSelect={false}
                            required
                            options={outseamOptions}
                            value={selection.outseam_measurements || []}
                            onChange={(selectedOptions) => {
                              updateColorSizeSelection(color, {
                                outseam_measurements: selectedOptions || [],
                              });
                            }}
                            placeholder={`Select ${color.name} outseam measurements`}
                            isSearchable
                            className="select"
                          />
                        </div>
                      </>
                    );
                  })()}
                </>
              )}

              {["Shirts", "Ties", "Belts"].includes(selectedCategory.name) && (
                <div>
                  <label className="form-label">
                    Select {color.name} Sizes <span>*</span>
                  </label>
                  <Select
                    name={`${colorKey}_sizes`}
                    isMulti
                    closeMenuOnSelect={false}
                    required
                    options={sizeOptions}
                    value={selection.sizes}
                    onChange={(selectedOptions) => {
                      updateColorSizeSelection(color, {
                        sizes: selectedOptions || [],
                      });
                    }}
                    placeholder={`Select ${color.name} sizes`}
                    isSearchable
                    className="select"
                  />
                </div>
              )}

              {["Bow Ties", "Pocket Square", "Socks"].includes(
                selectedCategory.name,
              ) && (
                  <div>
                    <label className="form-label">
                      {color.name} Size <span>*</span>
                    </label>
                    <input
                      type="text"
                      value="Standard"
                      disabled
                      className="form-control"
                    />
                  </div>
                )}

              {selectedCategory.name === "Shoes" && (
                <div>
                  <label className="form-label">
                    Select {color.name} Shoe Sizes <span>*</span>
                  </label>
                  <Select
                    name={`${colorKey}_shoeSizes`}
                    isMulti
                    closeMenuOnSelect={false}
                    required
                    options={shoeSizeOptions}
                    value={selection.shoe_sizes}
                    onChange={(selectedOptions) => {
                      updateColorSizeSelection(color, {
                        shoe_sizes: selectedOptions || [],
                      });
                    }}
                    placeholder={`Select ${color.name} shoe sizes`}
                    isSearchable
                    className="select"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`container mt-4 mainForm${selectedCategory?.name === "Suits" ? " suit" : ""}`}
      encType="multipart/form-data"
    >
      <div className="mb-3">
        <label htmlFor="name" className="form-label">
          Product Name <span>*</span>
        </label>
        <input
          type="text"
          className="form-control"
          id="name"
          name="name"
          value={formData.name}
          required
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, name: e.target.value }))
          }
          placeholder="Product Name"
        />
      </div>
      {/* <div className="mb-3">
        <label htmlFor="slug" className="form-label">
          Slug
        </label>
        <input
          type="text"
          className="form-control"
          id="slug"
          name="slug"
          value={formData.slug}
          readOnly
          disabled
          placeholder="Slug"
        />
      </div> */}

      <div className="mb-3">
        <label htmlFor="description" className="form-label">
          Description <span>*</span>
        </label>
        <ReactQuill
          theme="snow"
          value={formData.description}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, description: value }))
          }
          id="description"
          name="description"
        />
      </div>

      {/* Category Field */}
      <div className="mb-3">
        <label htmlFor="category" className="form-label">
          Category <span>*</span>
        </label>
        <Select
          name="category"
          id="category"
          required
          options={categoryOptions}
          value={categoryOptions.find(
            (opt) => opt.value === formData.category_id,
          )}
          onChange={handleCategoryChange}
          placeholder="Select a category"
          isSearchable
          isLoading={categoriesLoading}
          className="select"
        />
      </div>

      {/* Type Field */}
      <div className="mb-3">
        <label htmlFor="type" className="form-label">
          Product Type <span>*</span>
        </label>
        <Select
          name="type"
          id="type"
          required
          options={typeOptions}
          value={typeOptions.find((opt) => opt.value === formData.type) || null}
          onChange={handleTypeChange}
          placeholder="Select product type"
          isSearchable
          className="select"
        />
      </div>

      {(formData.type === "buy" || formData.type === "both") && (
        <div className="mb-3">
          <label htmlFor="buy_price" className="form-label">
            Buy Price ($) <span>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="buy_price"
            name="buy_price"
            value={formData.buy_price}
            required={formData.type === "buy" || formData.type === "both"}
            onChange={handleBuyPriceChange}
            placeholder="Enter buy price"
          />
        </div>
      )}
      {(formData.type === "rent" || formData.type === "both") && (
        <div className="mb-3">
          <label htmlFor="rent_price" className="form-label">
            Rent Price ($) <span>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="rent_price"
            name="rent_price"
            value={formData.rent_price}
            required={formData.type === "rent" || formData.type === "both"}
            onChange={handleRentPriceChange}
            placeholder="Enter rent price"
          />
        </div>
      )}

      {/* Color Fields */}
      <div className="row align-items-end">
        <div className="mb-3 col-md-6">
          <label htmlFor="color_name" className="form-label">
            Color Name <span>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="color_name"
            name="color_name"
            value={formData.color_name}
            required={formData.colors.length === 0}
            onChange={handleColorNameChange}
            placeholder="Color Name"
          />
        </div>
        <div className="mb-3 col-md-6">
          <label htmlFor="color_code" className="form-label">
            Color Code <span>*</span>
          </label>
          <div className="position-relative">
            <div
              className="d-flex align-items-center gap-2 rounded color-field"
              style={{ cursor: "pointer" }}
              onClick={() => setShowColorPicker(!showColorPicker)}
            >
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  backgroundColor: formData.color_code,
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
              <input
                type="text"
                className="form-control border-0 p-0"
                id="color_code"
                name="color_code"
                value={formData.color_code}
                required={formData.colors.length === 0}
                onChange={(e) => handleColorChange(e.target.value)}
                placeholder="#000000"
                style={{ boxShadow: "none", outline: "none" }}
              />
            </div>
            {showColorPicker && (
              <div className="position-absolute mt-2" style={{ zIndex: 1000 }}>
                <HexColorPicker
                  color={formData.color_code}
                  onChange={handleColorChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="row align-items-end">
        {(formData.type === "buy" || formData.type === "both") && (
          <div className="mb-3 col-md-5">
            <label htmlFor="buy_style" className="form-label">
              Buy Style <span>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="buy_style"
              name="buy_style"
              value={formData.buy_style}
              required={(formData.type === "buy" || formData.type === "both") && formData.colors.length === 0}
              onChange={handleBuyStyleChange}
              placeholder="Enter buy style"
            />
          </div>
        )}
        {(formData.type === "rent" || formData.type === "both") && (
          <div className="mb-3 col-md-5">
            <label htmlFor="rent_style" className="form-label">
              Rent Style <span>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="rent_style"
              name="rent_style"
              value={formData.rent_style}
              required={(formData.type === "rent" || formData.type === "both") && formData.colors.length === 0}
              onChange={handleRentStyleChange}
              placeholder="Enter rent style"
            />
          </div>
        )}
        {selectedCategory?.name !== "Suits" && (
          <div className="mb-3 col-md-2 color-button">
            <button
              type="button"
              className="designBtn"
              onClick={handleAddColor}
            >
              {editingColorIndex !== null ? "Update" : "Add"}
            </button>
          </div>
        )}
      </div>
      {selectedCategory?.name !== "Suits" && formData.colors.length > 0 && (
        <div className="mb-3 d-flex flex-wrap gap-2">
          {formData.colors.map((color, index) => (
            <div
              key={`${color.name}-${color.code}`}
              className="d-flex align-items-center color-box"
              onClick={() => handleEditColor(index)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleEditColor(index);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <span
                style={{
                  width: "18px",
                  height: "18px",
                  backgroundColor: color.code,
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  display: "inline-block",
                }}
              />
              <span className="d-flex flex-column lh-sm">
                <span>{color.name}</span>
                {(formData.type === "buy" || formData.type === "both") && color.buy_style && (
                  <small className="text-muted">Buy: {color.buy_style}</small>
                )}
                {(formData.type === "rent" || formData.type === "both") && color.rent_style && (
                  <small className="text-muted">Rent: {color.rent_style}</small>
                )}
              </span>
              <button
                type="button"
                className="designBtn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveColor(index);
                }}
                aria-label={`Remove ${color.name}`}
              >
                x
              </button>
            </div>
          ))}
        </div>
      )}

      {renderColorSizeFields()}

      {/* Featured Image Field */}
      <div className="mb-3">
        <div className="row">
          <div className="col-md-6 mb-3 mb-md-0 featured-image-field">
            <label htmlFor="featured_image" className="form-label">
              Featured Image <span>*</span>
            </label>
            <div className="file-picker-control">
              <button
                type="button"
                className="designBtn file-picker-button"
                onClick={() => featuredImageInputRef.current?.click()}
              >
                Choose File
              </button>
              <span className="file-picker-name">
                {formData.featured_image?.name || "No file chosen"}
              </span>
            </div>
            <input
              ref={featuredImageInputRef}
              type="file"
              className="visually-hidden-file-input"
              id="featured_image"
              name="featured_image"
              accept="image/*"
              onChange={handleFeaturedImageChange}
            />
          </div>
          {selectedCategory?.name !== "Suits" && (
            <div className="col-md-6">
              <label htmlFor="featured_image_color" className="form-label">
                Featured Image Color <span>*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="featured_image_color"
                name="featured_image_color"
                required
                list="product-image-color-options"
                value={formData.featured_image_color?.label || ""}
                onChange={handleFeaturedImageColorChange}
                placeholder="Color Name"
              />
            </div>
          )}
        </div>
        {featuredImagePreview && (
          <div
            className="mt-2 position-relative images"
            style={{ width: "100px" }}
          >
            <img src={featuredImagePreview} alt="Featured preview" />
            <button
              type="button"
              className="btn position-absolute top-0 end-0"
              onClick={removeFeaturedImage}
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Gallery Images Field */}
      <div className="mb-5">
        <div className="row align-items-end">
          <div className="col-md-6 mb-3 gallery-images-field">
            <label htmlFor="gallery_images" className="form-label">
              Gallery Images
            </label>
            <div className="file-picker-control">
              <button
                type="button"
                className="designBtn file-picker-button"
                onClick={() => galleryImagesInputRef.current?.click()}
              >
                Choose Files
              </button>
              <span className="file-picker-name">
                {pendingGalleryImages.length > 0
                  ? `${pendingGalleryImages.length} file(s) chosen`
                  : "No files chosen"}
              </span>
            </div>
            <input
              ref={galleryImagesInputRef}
              type="file"
              className="visually-hidden-file-input"
              id="gallery_images"
              name="gallery_images"
              accept="image/*"
              multiple
              onChange={handleGalleryImagesChange}
            />
          </div>
          {selectedCategory?.name !== "Suits" && (
            <div className="col-md-4 mb-3">
              <label htmlFor="gallery_image_color" className="form-label">
                Gallery Image Color
              </label>
              <Select
                id="gallery_image_color"
                name="gallery_image_color"
                options={getColorOptions()}
                value={pendingGalleryImageColor}
                onChange={handlePendingGalleryImageColorChange}
                placeholder="Select color"
                isSearchable
                className="select"
              />
            </div>
          )}
          <div className="col-md-2 image-button">
            <button
              type="button"
              className="designBtn"
              onClick={handleAddGalleryImages}
            >
              Add
            </button>
          </div>
        </div>
        <datalist id="product-image-color-options">
          {getColorOptions().map((color) => (
            <option key={`${color.label}-${color.code}`} value={color.label} />
          ))}
        </datalist>
        <div className="d-flex flex-wrap gap-2">
          {galleryImagePreviews.length > 0 && (
            <div className="mt-2 d-flex flex-wrap gap-2">
              {galleryImagePreviews.map((preview, index) => (
                <div
                  key={index}
                  className="position-relative images"
                  style={{ width: "100px" }}
                >
                  <img src={preview} alt={`Gallery ${index + 1}`} />
                  {selectedCategory?.name !== "Suits" && formData.gallery_image_colors[index] && (
                    <span
                      className="position-absolute bottom-0 start-0 text-white px-1"
                      style={{
                        backgroundColor: "rgba(0,0,0,0.65)",
                        fontSize: "11px",
                        maxWidth: "100%",
                      }}
                    >
                      {formData.gallery_image_colors[index].label}
                    </span>
                  )}
                  <button
                    type="button"
                    className="btn position-absolute top-0 end-0"
                    onClick={() => removeGalleryImage(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mb-3 form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id="is_featured"
          name="is_featured"
          checked={formData.is_featured}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, is_featured: e.target.checked }))
          }
        />
        <label className="form-check-label" htmlFor="is_featured">
          Mark as Featured Product
        </label>
      </div>

      <button type="submit" className="btn btn-primary" disabled={submitting}>
        {submitting ? "Creating Product..." : "Create Product"}
      </button>
    </form>
  );
};

export default AddProductForm;
